// src/components/calendar/Calendar.jsx
import React, { useMemo } from 'react';

// ─── Constants ────────────────────────────────────────────────────────────────

const DAY_NAMES = [
  { full: 'שני',    short: 'ב' },
  { full: 'שלישי', short: 'ג' },
  { full: 'רביעי', short: 'ד' },
  { full: 'חמישי', short: 'ה' },
  { full: 'שישי',  short: 'ו' },
  { full: 'שבת',   short: 'ש' },
  { full: 'ראשון', short: 'א' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const today = new Date();
today.setHours(0, 0, 0, 0);
const todayString = formatDate(today);

// ─── Sub-components ───────────────────────────────────────────────────────────

const NavButton = ({ onClick, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
  >
    {children}
  </button>
);

// ─── Component ────────────────────────────────────────────────────────────────

const Calendar = ({
  currentMonth, prevMonth, nextMonth,
  getDaysInMonth, getFirstDayOffset,
  handleDateSelect, selectedDate, availableSlots,
}) => {

  const onDateSelect = (date) => {
    const str = formatDate(date);
    if (availableSlots[str]?.length > 0 && date >= today) {
      handleDateSelect(str);
    }
  };

  const days = useMemo(() => getDaysInMonth(), [getDaysInMonth, currentMonth]);
  const offset = useMemo(() => getFirstDayOffset(), [getFirstDayOffset, currentMonth]);

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">

      {/* Header */}
      <div className="bg-gradient-to-r from-amber-800 to-amber-400 px-4 sm:px-5 py-3 sm:py-4 text-white">
        <div className="flex items-center justify-between">
          <NavButton onClick={prevMonth} label="חודש קודם">&#8594;</NavButton>
          <h3 className="text-base sm:text-lg font-bold select-none">
            {currentMonth.toLocaleDateString('he-IL', { month: 'long', year: 'numeric' })}
          </h3>
          <NavButton onClick={nextMonth} label="חודש הבא">&#8592;</NavButton>
        </div>
      </div>

      {/* Grid */}
      <div className="p-3 sm:p-4 md:p-5">

        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 mb-2">
          {DAY_NAMES.map((d) => (
            <div key={d.full} className="text-center text-xs sm:text-sm font-medium text-gray-400 py-1 select-none">
              <span className="hidden sm:inline">{d.full}</span>
              <span className="sm:hidden">{d.short}</span>
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">

          {/* Empty offset cells */}
          {Array.from({ length: offset }).map((_, i) => (
            <div key={`e-${i}`} className="h-9 sm:h-11 md:h-14" />
          ))}

          {/* Day cells */}
          {days.map((date) => {
            const str      = formatDate(date);
            const slots    = availableSlots[str]?.length ?? 0;
            const isAvail  = slots > 0;
            const isPast   = date < today;
            const isSelected = selectedDate === str;
            const isToday    = todayString === str;
            const clickable  = isAvail && !isPast;

            return (
              <div
                key={str}
                role="button"
                tabIndex={clickable ? 0 : -1}
                onClick={() => onDateSelect(date)}
                onKeyDown={(e) => e.key === 'Enter' && onDateSelect(date)}
                aria-pressed={isSelected}
                aria-disabled={!clickable}
                className={[
                  'h-9 sm:h-11 md:h-14 rounded-xl flex flex-col items-center justify-center border transition-colors select-none',
                  isSelected
                    ? 'bg-indigo-600 text-white border-indigo-500'
                    : isToday
                    ? 'bg-indigo-50 border-indigo-300'
                    : clickable
                    ? 'border-gray-200 hover:bg-indigo-50 hover:border-indigo-300 cursor-pointer'
                    : 'border-gray-100 bg-gray-50 text-gray-300 cursor-default',
                ].join(' ')}
              >
                <span className="text-xs sm:text-sm md:text-base font-bold leading-none">
                  {date.getDate()}
                </span>

                {/* Slot dots */}
                {clickable && (
                  <div className="flex gap-px mt-0.5 sm:mt-1">
                    {Array.from({ length: Math.min(3, slots) }).map((_, i) => (
                      <div
                        key={i}
                        className={`w-0.5 h-0.5 sm:w-1 sm:h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-green-500'}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Calendar;